import { useContext, useEffect, useState } from 'react'

import { Avatar, Box, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'

import { navigate, routes } from '@redwoodjs/router'
import { useQuery } from '@redwoodjs/web'

import CallTags, {
  CallActorBadge,
} from 'src/components/ActivityLogs/CallLog/CallTags'
import ActorBadge from 'src/components/ActivityLogs/shared/ActorBadge'
import DnTable from 'src/components/Core/DnTable'
import MentionText from 'src/components/DataDisplay/MentionText/MentionText'
import { SearchBox } from 'src/components/Inputs/SearchBox/SearchBox'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useActivityLogSidebarContext } from 'src/context/ActivityLogSidebarContext'
import { useCallDrawerContext } from 'src/context/CallDrawerContext'
import { useReadOnlyEmailModalContext } from 'src/context/ReadOnlyEmailModalContext'
import { RecruitersContext } from 'src/context/RecruitersContext'
import { useTaskModalContext } from 'src/context/TaskModalContext'
import { GET_ACTIVITY_LOGS } from 'src/graphql/activityLogs.graphql'
import {
  ActivityLogActorType,
  CallDirection,
  CallType,
  SMSDirection,
} from 'src/graphql/types/activityLogs'
import { EmailDirection } from 'src/graphql/types/emails'
import IconApplicant from 'src/icons/IconApplicant'
import IconChecklist from 'src/icons/IconChecklist'
import IconDocument from 'src/icons/IconDocument'
import IconEmail from 'src/icons/IconEmail'
import IconFileCabinet from 'src/icons/IconFileCabinet'
import IconHamburger from 'src/icons/IconHamburger'
import IconMessage from 'src/icons/IconMessage'
import IconNotes from 'src/icons/IconNotes'
import IconPhoneCall from 'src/icons/IconPhoneCall'
import { buildFullName } from 'src/lib/formatters'
import { replaceRecruitersIdWithName } from 'src/lib/recruiters.utils'
import { getNegativeVoEOutcomeLabel } from 'src/lib/voe/callOutcomeTag'
import CustomTab from 'src/pages/ActivitiesPage/components/CustomTab'

const onError = () => {
  toast('Unable to get activity logs, please try again.', 'error')
}

const LIMIT = 20

// Maps filter values to the activity log types they include
const FILTER_TYPE_MAP: Record<string, string[]> = {
  ALL: [],
  CALL: ['CALL'],
  SMS: ['SMS'],
  EMAIL: ['EMAIL'],
  NOTE: ['NOTE'],
  TASK: ['TASK'],
  DOCUMENT: ['DOCUMENT'],
}

// Maps activity log types to their display icon and label
const TYPE_ICON_MAP: Record<
  string,
  { icon: React.FC<{ className?: string }>; label: string; fillMode: string }
> = {
  CALL: { icon: IconPhoneCall, label: 'Call', fillMode: 'stroke' },
  SMS: { icon: IconMessage, label: 'Text', fillMode: 'stroke' },
  EMAIL: { icon: IconEmail, label: 'Email', fillMode: 'stroke' },
  NOTE: { icon: IconNotes, label: 'Note', fillMode: 'fill' },
  TASK: { icon: IconChecklist, label: 'Task', fillMode: 'stroke' },
  DOCUMENT: { icon: IconFileCabinet, label: 'Document', fillMode: 'stroke' },
  APPLICANT: { icon: IconApplicant, label: 'Applicant', fillMode: 'stroke' },
  APPLICATION: { icon: IconDocument, label: 'Application', fillMode: 'stroke' },
  ADVERSE_ACTION: {
    icon: IconDocument,
    label: 'Adverse Action',
    fillMode: 'stroke',
  },
}

const FILTER_TABS: {
  value: string
  label: string
  icon: React.FC<{ className?: string }>
  fillMode?: string
}[] = [
  { value: 'ALL', label: 'All', icon: IconHamburger },
  { value: 'CALL', label: 'Calls', icon: IconPhoneCall },
  { value: 'SMS', label: 'Texts', icon: IconMessage },
  { value: 'EMAIL', label: 'Emails', icon: IconEmail },
  { value: 'NOTE', label: 'Notes', icon: IconNotes, fillMode: 'fill' },
  { value: 'TASK', label: 'Tasks', icon: IconChecklist },
  { value: 'DOCUMENT', label: 'Documents', icon: IconFileCabinet },
]

const FilterTab = ({
  activeFilter,
  value,
  label,
  icon: Icon,
  setActiveFilter,
  fillMode = 'stroke',
}: {
  activeFilter: string
  value: string
  label: string
  icon: React.FC<{ className?: string }>
  setActiveFilter: (value: string) => void
  fillMode?: string
}) => {
  const isSelected = activeFilter === value

  return (
    <Tooltip label={label} position="bottom" withArrow>
      <Box>
        <CustomTab
          selectedTab={activeFilter}
          value={value}
          setSelectedTab={setActiveFilter}
          variant="segmented"
          className="px-2.5"
          label={
            <Icon
              className={`h-4 w-4 ${
                fillMode === 'fill'
                  ? isSelected
                    ? 'fill-doubleNickel-gray-700'
                    : 'fill-doubleNickel-gray-500'
                  : `fill-none ${
                      isSelected
                        ? 'stroke-doubleNickel-gray-700'
                        : 'stroke-doubleNickel-gray-500'
                    }`
              }`}
            />
          }
        />
      </Box>
    </Tooltip>
  )
}

const TypeIcon = ({ type }: { type: string }) => {
  const config = TYPE_ICON_MAP[type]
  if (!config) return null

  const { icon: Icon, label, fillMode } = config

  return (
    <Tooltip label={label} position="top" withArrow>
      <Box className="flex items-center">
        <Icon
          className={`h-3.5 w-3.5 ${
            fillMode === 'fill'
              ? 'fill-doubleNickel-gray-500'
              : 'fill-none stroke-doubleNickel-gray-500'
          }`}
        />
      </Box>
    </Tooltip>
  )
}

const EmployeeAvatar = ({ employee }: { employee: any }) => {
  if (!employee) {
    return <span className="text-doubleNickel-gray-400">-</span>
  }

  const name = buildFullName(employee)
  const initials = `${(employee.firstName || '').charAt(0)}${(
    employee.lastName || ''
  ).charAt(0)}`

  return (
    <Tooltip label={name} withArrow>
      <Avatar
        classNames={{
          placeholder: 'bg-doubleNickel-brand-25 text-doubleNickel-brand-500',
        }}
        size="sm"
        radius="sm"
      >
        {initials}
      </Avatar>
    </Tooltip>
  )
}

const SourceCell = ({ row }: { row: any }) => {
  // Inbound texts/calls/emails were initiated by the applicant — nobody
  // internal (human or automation) "did" them, so Source stays the neutral
  // dash. The employee on these rows is just whose line/inbox received it.
  // For emails, prefer the Email record's direction; rows without the email
  // relation fall back to the title every writer hardcodes ('Email Received').
  const isInboundEmail =
    row.type === 'EMAIL' &&
    (row.email?.direction
      ? row.email.direction === EmailDirection.INBOUND
      : row.title === 'Email Received')

  if (
    row.smsDirection === SMSDirection.INBOUND ||
    row.callDirection === CallDirection.INBOUND ||
    isInboundEmail
  ) {
    return <span className="text-doubleNickel-gray-400">-</span>
  }

  // AI voice agent calls read as Auto here — Source only answers "human or
  // automation?"; the AI pill in the Content cell names the agent on hover.
  const isAICall = row.callType === CallType.BLANDAI
  const isAutomated =
    row.actorType === ActivityLogActorType.AUTOMATED_RULE ||
    row.actorType === ActivityLogActorType.SYSTEM

  if (isAutomated || isAICall) {
    return (
      <ActorBadge
        variant="auto"
        label={
          isAICall
            ? 'Placed automatically by an AI voice agent'
            : 'Performed automatically by a rule'
        }
      />
    )
  }

  // Bulk actions show only the pill — the avatar is reserved for actions an
  // employee sent individually. The tooltip still credits who ran the bulk.
  if (row.actorType === ActivityLogActorType.BULK_ACTION) {
    const bulkActorName = row.employee ? buildFullName(row.employee) : null
    return (
      <ActorBadge
        variant="bulk"
        label={
          bulkActorName
            ? `Bulk action by ${bulkActorName}`
            : 'Part of a bulk action'
        }
      />
    )
  }

  // MANUAL and legacy rows without an actorType
  return <EmployeeAvatar employee={row.employee} />
}

const TruncatedTitle = ({ title }: { title?: string | null }) => {
  // Only show the tooltip when the text actually overflows the (resizable)
  // column — measured lazily on hover since widths change as the user drags.
  const [isOverflowing, setIsOverflowing] = useState(false)

  return (
    <Tooltip label={title} withArrow disabled={!title || !isOverflowing}>
      <div
        className="min-w-0 truncate"
        onMouseEnter={(e) => {
          const el = e.currentTarget
          setIsOverflowing(el.scrollWidth > el.clientWidth)
        }}
      >
        {title}
      </div>
    </Tooltip>
  )
}

const ActivityLogTab = ({ applicant, activityType = 'ALL' }) => {
  const recruiters = useContext(RecruitersContext)
  const { handleOpen: openCallDrawer } = useCallDrawerContext()
  const { handleOpen: openEmailModal } = useReadOnlyEmailModalContext()
  const { handleOpen: openTaskModal } = useTaskModalContext() ?? {}
  const { setSelectedTab } = useActivityLogSidebarContext() ?? {}
  const [activeFilter, setActiveFilter] = useState(activityType)
  const [searchText, setSearchText] = useState('')
  const [filters, setFilters] = useState({
    createdAt: null,
    activityLogTypes: [] as string[],
    searchText: '',
  })
  const applicantId = applicant?.applicantId || null

  const {
    data: activityLogsResponse,
    loading,
    refetch,
  } = useQuery(GET_ACTIVITY_LOGS, {
    skip: !applicantId,
    variables: {
      applicantId,
      filters,
      pageInfo: { offset: 0, limit: LIMIT },
    },
    onError,
  })

  const totalCount = activityLogsResponse?.activityLogs?.totalCount ?? 0
  const items = activityLogsResponse?.activityLogs?.items ?? []

  // Sync activeFilter with route-based activityType
  useEffect(() => {
    setActiveFilter(activityType)
  }, [activityType])

  // Update filters when activeFilter changes
  useEffect(() => {
    const types = FILTER_TYPE_MAP[activeFilter] ?? []
    setFilters((prev) => ({
      ...prev,
      activityLogTypes: types,
    }))
  }, [activeFilter])

  const handleFilterChange = (value: string) => {
    setActiveFilter(value)
    navigate(
      routes.applicantDetails({
        id: applicantId,
        tab: 'activitylog',
        activityType: value,
      })
    )
  }

  const onSearch = (text: string) => {
    setFilters((prev) => ({
      ...prev,
      searchText: text,
    }))
  }

  const handleLoadPage = ({
    offset,
    limit,
  }: {
    offset: number
    limit: number
  }) => {
    refetch({
      applicantId,
      filters,
      pageInfo: { offset, limit },
    })
  }

  const handleRowClick = (_rowIndex: number, rowData: any) => {
    if (rowData.type === 'CALL') {
      openCallDrawer(rowData.activityLogId)
    } else if (rowData.type === 'SMS') {
      setSelectedTab?.('SMS')
    } else if (rowData.type === 'EMAIL') {
      if (rowData.email?.emailThreadId) {
        openEmailModal({
          emailThreadId: rowData.email.emailThreadId,
          selectedEmailId: rowData.emailId,
        })
      } else {
        toast('Email thread not found', 'error')
      }
    } else if (rowData.type === 'NOTE') {
      setSelectedTab?.('NOTE')
    } else if (rowData.type === 'TASK') {
      if (rowData.taskId) {
        openTaskModal?.({ id: rowData.taskId })
      } else {
        toast('Task not found', 'error')
      }
    }
  }

  const connectedStatusMap: Record<string, string> = {
    BOOKED: 'Appointment booked',
    NOT_INTERESTED: 'Not interested',
    NOT_AVAILABLE: 'Not available',
    DISQUALIFIED: 'Disqualified',
    TRANSFERRED: 'Transferred',
    VERIFIED: 'Verified',
    INFO_COLLECTED: 'Info collected',
    VOICEMAIL: 'Voicemail',
  }

  const renderContent = (row: any) => {
    const content = row.content || ''

    // VoE calls: show company name, who actually made the call, and the
    // disposition when the agent recorded one (Verified, Voicemail, …)
    if (row.voeId && row.voe?.companyName) {
      const lead =
        row.callType === 'BLANDAI'
          ? `Voice agent call to ${row.voe.companyName}`
          : `Verification call to ${row.voe.companyName}`
      const outcome =
        getNegativeVoEOutcomeLabel(row.connectedStatus, row.voeFindings) ??
        (row.connectedStatus
          ? connectedStatusMap[row.connectedStatus]
          : undefined)
      return outcome ? `${lead} \u00b7 ${outcome}` : lead
    }

    // Calls: show duration and outcome
    if (row.type === 'CALL') {
      const parts: string[] = []

      if (row.callDuration && row.callDuration > 0) {
        const minutes = Math.floor(row.callDuration / 60)
        const seconds = row.callDuration % 60
        parts.push(`${minutes}m ${seconds}s`)
      }

      if (row.connectedStatus && connectedStatusMap[row.connectedStatus]) {
        parts.push(connectedStatusMap[row.connectedStatus])
      }

      // Failed/missed calls have no duration or connected status \u2014 fall back
      // to the stored content ('Failed', 'Voicemail left', 'No answer', \u2026) so
      // the outcome isn't lost from the Content cell.
      const callParts = parts.filter(Boolean)
      return callParts.length > 0 ? callParts.join(' \u00b7 ') : content
    }

    // SMS: MMS messages can be attachment-only (no text), which would leave
    // the Content column empty — surface the attachment(s) instead. When
    // there's text too, append an attachment count after it.
    if (row.type === 'SMS') {
      const attachmentCount = row.documents?.length ?? 0
      if (attachmentCount > 0) {
        if (!content) {
          return attachmentCount === 1
            ? `\u{1F4CE} ${row.documents[0].fileName || '1 attachment'}`
            : `\u{1F4CE} ${attachmentCount} attachments`
        }
        return `${content} · \u{1F4CE} ${attachmentCount} ${
          attachmentCount === 1 ? 'attachment' : 'attachments'
        }`
      }
      return content
    }

    if (row.type === 'NOTE') {
      return replaceRecruitersIdWithName(content, recruiters)
    }
    return content
  }

  const columns = [
    {
      key: 'createdAt',
      label: 'Date',
      width: 15, // 15%
      minWidth: 10, // 10%
      render: (row: any) => dayjs(row.createdAt).format('MM/DD/YYYY hh:mm A'),
    },
    {
      key: 'source',
      label: 'Source',
      width: 8, // 8%
      minWidth: 6, // 6%
      render: (row: any) => <SourceCell row={row} />,
    },
    {
      key: 'type',
      label: 'Type',
      width: 5, // 5%
      minWidth: 5, // 5%
      render: (row: any) => <TypeIcon type={row.type} />,
    },
    {
      key: 'title',
      label: 'Title',
      width: 15, // 15%
      minWidth: 8, // 8%
      render: (row: any) => (
        <TruncatedTitle title={row.voeId ? 'Outbound Call' : row.title} />
      ),
    },
    {
      key: 'content',
      label: 'Content',
      width: 57, // 57%
      minWidth: 20, // 20%
      render: (row: any) => {
        const content = renderContent(row)
        const text =
          row.type === 'NOTE' ? (
            <MentionText content={content} recruiters={recruiters} />
          ) : (
            content
          )
        return (
          <Tooltip
            label={content}
            withArrow
            multiline
            maw={400}
            disabled={!content || content.length < 60}
          >
            <div
              className={`flex flex-row items-center${
                row.type === 'CALL' ? ' gap-2' : ''
              }`}
            >
              <div className="line-clamp-1 min-w-0 whitespace-pre-line">
                {text}
              </div>
              {row.type === 'CALL' && (
                <>
                  <CallActorBadge activityLog={row} />
                  <CallTags activityLog={row} />
                </>
              )}
            </div>
          </Tooltip>
        )
      },
    },
  ]

  return (
    <div className="flex h-[90%] flex-row gap-10">
      <div className="flex h-full w-full flex-col gap-4 overflow-auto">
        <div className="relative flex flex-col justify-between gap-4">
          <div className="flex flex-1 flex-row items-center gap-4">
            <div className="flex flex-col text-lg font-semibold text-doubleNickel-black">
              Activity Log
              <div className="text-sm font-normal text-doubleNickel-gray-600">
                {totalCount} {totalCount === 1 ? 'event' : 'events'}
              </div>
            </div>

            <div className="ml-auto flex flex-row items-center gap-3">
              <SearchBox
                className="w-64"
                size="xs"
                placeholder="Search activity logs"
                onSearch={onSearch}
                value={searchText}
                setValue={setSearchText}
              />
              <div className="flex items-center gap-0.5 rounded-lg bg-doubleNickel-gray-100 p-0.5">
                {FILTER_TABS.map((tab) => (
                  <FilterTab
                    key={tab.value}
                    activeFilter={activeFilter}
                    value={tab.value}
                    label={tab.label}
                    icon={tab.icon}
                    setActiveFilter={handleFilterChange}
                    fillMode={tab.fillMode}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <DnTable
            columns={columns}
            data={items}
            loading={loading}
            totalCount={totalCount}
            onLoadPage={handleLoadPage}
            onRowClick={handleRowClick}
            enableColumnResize={true}
            enablePagination={true}
            defaultResultsPerPage={LIMIT}
            emptyStateMessage="No activity logs found"
            headerClassName="text-xs font-semibold text-doubleNickel-gray-700"
            rowClassName="text-[11px]"
          />
        </div>
      </div>
    </div>
  )
}

export default ActivityLogTab
